<template>
  <div class="product-category-management">
    <!-- Add this right after the add/edit modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="confirm-modal">
        <h3>Confirm Deletion</h3>
        <p>
          Are you sure you want to delete {{ categoryToDelete.name }}?, This
          action cannot be undone.
        </p>
        <div class="confirm-buttons">
          <button @click="showDeleteModal = false" class="btn-secondary">
            Cancel
          </button>
          <button @click="deleteCategory" class="btn-danger">Delete</button>
        </div>
      </div>
    </div>

    <FlashMessage ref="flashMessage" />
    <LoadingIndicator v-if="loading" message="Loading categories..." />

    <!-- Header with title and add category button -->
    <div class="header">
      <h1>Product Categories</h1>
      <button class="btn-primary" @click="showAddModal = true">
        <i class="fas fa-plus"></i> Add Category
      </button>
    </div>

    <!-- Search bar -->
    <div class="control-bar">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search categories..."
          @input="applyFilters"
        />
      </div>
    </div>

    <!-- Categories table -->
    <div class="table-responsive">
      <table class="categories-table">
        <thead>
          <tr>
            <th @click="sortBy('id')">ID <i :class="sortIcon('id')"></i></th>
            <th @click="sortBy('name')">
              Name <i :class="sortIcon('name')"></i>
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="category in filteredCategories" :key="category.id">
            <td>{{ category.id }}</td>
            <td>{{ category.name }}</td>
            <td class="actions">
              <button
                @click="editCategory(category)"
                class="btn-icon"
                title="Edit"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                @click="confirmDelete(category)"
                class="btn-icon"
                title="Delete"
              >
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        v-if="filteredCategories.length === 0 && !loading"
        class="empty-state"
      >
        <p>
          No categories found. Try adjusting your search or add a new category.
        </p>
      </div>
    </div>

    <CategoryFormModal
      v-if="showAddModal"
      :show="showAddModal"
      :editingCategory="editingProduct"
      :apiCategories="categories"
      :initialFormData="formData"
      @close="closeModal"
      @submit="handleFormSubmit"
    />

    <!-- Export buttons -->
    <div class="export-actions" v-if="filteredCategories.length > 0">
      <button @click="exportToCSV" class="btn-export">
        <i class="fas fa-file-csv"></i> CSV
      </button>
      <button @click="exportToExcel" class="btn-export">
        <i class="fas fa-file-excel"></i> Excel
      </button>
      <button @click="printTable" class="btn-export">
        <i class="fas fa-print"></i> Print
      </button>
      <button @click="exportToPDF" class="btn-export">
        <i class="fas fa-file-pdf"></i> PDF
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from "vue";
import {
  GetProductCategory,
  createCategory,
  updateCategoryRequest,
  DeleteCategory as apiDeleteCategory,
} from "@/API-requests/Requests";
import FlashMessage from "@/components/FlashMessage.vue";
import LoadingIndicator from "@/components/Loading_indicator.vue";
import CategoryFormModal from "@/components/CategoryFormModal.vue";

// Refs
const categories = ref([]);
const loading = ref(true);
const flashMessage = ref(null);

// Form data
const formData = ref({ name: "" });

// UI state
const showAddModal = ref(false);
const showDeleteModal = ref(false);
const editingCategory = ref(null);
const categoryToDelete = ref(null);

// Filter/Sort/Pagination
const searchQuery = ref("");
const sortField = ref("id");
const sortDirection = ref("asc"); // <-- Declare `sortDirection` as a `ref`
const currentPage = ref(1);
const itemsPerPage = 10;

//as were now dealing with categories main category
const apiCategories = ref([]);

// Computed properties
const filteredCategories = computed(() => {
  let result = [...categories.value];

  // Apply search
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      (c) =>
        c.name.toLowerCase().includes(query) || c.id.toString().includes(query)
    );
  }

  // Apply sorting
  result.sort((a, b) => {
    if (a[sortField.value] < b[sortField.value]) {
      return sortDirection.value === "asc" ? -1 : 1;
    }
    if (a[sortField.value] > b[sortField.value]) {
      return sortDirection.value === "asc" ? 1 : -1;
    }
    return 0;
  });

  // Apply pagination
  const start = (currentPage.value - 1) * itemsPerPage;
  return result.slice(start, start + itemsPerPage);
});

const totalPages = computed(() => {
  return Math.ceil(categories.value.length / itemsPerPage);
});

const loadInitialData = async () => {
  try {
    loading.value = true;
    await nextTick();

    const categoriesData = await GetProductCategory();
    categories.value = categoriesData.map((category) => ({
      id: category.id,
      name: category.name,
    }));

    if (flashMessage.value) {
      flashMessage.value.showFlashMessage(
        "success",
        "Categories loaded successfully"
      );
    }
  } catch (error) {
    console.error("Error loading categories:", error);
    flashMessage.value.showFlashMessage("error", "Failed to load categories");
  } finally {
    loading.value = false;
  }
};

    // apiCategories.value = categoriesData.categories.map((category) => ({
    //   id: category.id,
    //   name: category.name,
    //   brands: category.children.map((brand) => ({
    //     id: brand.id,
    //     name: brand.name,
    //   })),
    // }));

const editCategory = (category) => {
  editingCategory.value = category;
  formData.value = { name: category.name };
  showAddModal.value = true;
};

const resetForm = () => {
  formData.value = { name: "" };
};

const closeModal = () => {
  showAddModal.value = false;
  editingCategory.value = null;
  resetForm();
};

const addCategory = async (formData) => {
  try {
    loading.value = true;
    const newCategory = await createCategory(formData);
    categories.value.push(newCategory);
    flashMessage.value.showFlashMessage(
      "success",
      "Category added successfully"
    );
    closeModal();
  } catch (error) {
    console.error("Error adding category:", error);
    flashMessage.value.showFlashMessage("error", "Failed to add category");
  } finally {
    loading.value = false;
  }
};

const updateCategory = async (formData) => {
  try {
    loading.value = true;
    await updateCategoryRequest(editingCategory.value.id, {
      name: formData.name,
    });
    const index = categories.value.findIndex(
      (c) => c.id === editingCategory.value.id
    );
    if (index !== -1) {
      categories.value[index].name = formData.name;
    }
    flashMessage.value.showFlashMessage(
      "success",
      "Category updated successfully"
    );
    closeModal();
  } catch (error) {
    console.error("Error updating category:", error);
    flashMessage.value.showFlashMessage("error", "Failed to update category");
  } finally {
    loading.value = false;
  }
};

const confirmDelete = (category) => {
  categoryToDelete.value = category;
  showDeleteModal.value = true;
};

const deleteCategory = async () => {
  try {
    loading.value = true;
    await apiDeleteCategory(categoryToDelete.value.id);
    categories.value = categories.value.filter(
      (c) => c.id !== categoryToDelete.value.id
    );
    flashMessage.value.showFlashMessage(
      "success",
      "Category deleted successfully"
    );
  } catch (error) {
    console.error("Error deleting category:", error);
    flashMessage.value.showFlashMessage("error", "Failed to delete category");
  } finally {
    loading.value = false;
    showDeleteModal.value = false;
    categoryToDelete.value = null;
  }
};

const sortIcon = (field) => {
  if (sortField.value !== field) return "fas fa-sort";
  return sortDirection.value === "asc" ? "fas fa-sort-up" : "fas fa-sort-down";
};

const handleFormSubmit = async (formData) => {
  try {
    if (editingCategory.value && editingCategory.value.id) {
      // This means we are updating an existing category
      await updateCategory({
        ...formData,
        id: editingCategory.value.id,
      });
    } else {
      // This means we are creating a new category
      await addCategory(formData);
    }
    flashMessage.value.showFlashMessage(
      "success",
      editingCategory.value && editingCategory.value.id
        ? "Category updated successfully"
        : "Category added successfully"
    );
    closeModal();
  } catch (error) {
    console.error("Form submission error:", error);
    flashMessage.value.showFlashMessage(
      "error",
      error.message || "Failed to save category"
    );
  }
};

// Filter/Sort methods
const sortBy = (field) => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

onMounted(loadInitialData);
</script>

<style scoped>
.product-management {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

h1 {
  font-size: 1.8rem;
  color: #2c3e50;
  margin: 0;
}

.control-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: center;
}

.search-box {
  position: relative;
  flex: 1;
  min-width: 250px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.3s;
}

.search-box input:focus {
  outline: none;
  border-color: #3498db;
}

.filters {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.filters select {
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  cursor: pointer;
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.products-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
}

.products-table th,
.products-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.products-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #2c3e50;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}

.products-table th:hover {
  background-color: #f1f3f5;
}

.products-table tr:hover td {
  background-color: #f8f9fa;
}

.thumbnail {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
}

.status-badge.active {
  background-color: #d4edda;
  color: #155724;
}

.status-badge.draft {
  background-color: #fff3cd;
  color: #856404;
}

.status-badge.archived {
  background-color: #f8d7da;
  color: #721c24;
}

.low-stock {
  color: #dc3545;
  font-weight: 500;
}

.stock-warning {
  display: inline-block;
  width: 18px;
  height: 18px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  text-align: center;
  line-height: 18px;
  font-size: 0.75rem;
  margin-left: 0.25rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary,
.btn-danger {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background-color: #3498db;
  color: white;
}

.btn-primary:hover {
  background-color: #2980b9;
}

.btn-secondary {
  background-color: #f8f9fa;
  color: #2c3e50;
  border: 1px solid #ddd;
}

.btn-secondary:hover {
  background-color: #e9ecef;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
}

.btn-danger:hover {
  background-color: #c0392b;
}

.btn-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #f8f9fa;
  color: #7f8c8d;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.btn-icon:hover {
  background-color: #e9ecef;
  color: #2c3e50;
}

.pagination {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 2rem;
}

.pagination-btn {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #ddd;
  background-color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f8f9fa;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-btn.active {
  background-color: #3498db;
  color: white;
  border-color: #3498db;
}

.modal-overlay {
  position: fixed; /* Ensure the modal overlays on top */
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5); /* Semi-transparent background */
  z-index: 9999; /* Ensure it appears on top of all other content */
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
}

.confirm-modal {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); /* Add a subtle shadow for depth */
  animation: fadeIn 0.3s ease-out; /* Smooth fade-in effect */
}

.confirm-buttons {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.confirm-buttons button {
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  border-radius: 5px;
  cursor: pointer;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
  border: none;
  transition: background-color 0.3s ease;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.btn-danger {
  background-color: #e74c3c;
  color: white;
  border: none;
  transition: background-color 0.3s ease;
}

.btn-danger:hover {
  background-color: #c0392b;
}

@keyframes fadeIn {
  0% {
    opacity: 0;
    transform: scale(0.9);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-group.full-width {
    grid-column: span 1;
  }

  .header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .control-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filters {
    flex-direction: column;
    align-items: stretch;
  }

  .products-table th,
  .products-table td {
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
  }

  .actions {
    flex-wrap: wrap;
  }
}

@media (max-width: 576px) {
  .product-management {
    padding: 1rem;
  }

  .modal-content {
    width: 95%;
  }

  .modal-footer {
    flex-direction: column;
  }

  .modal-footer button {
    width: 100%;
  }
}

/* Export button styles */
.export-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.btn-export {
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
}

.btn-export:hover {
  background-color: #f5f5f5;
}

.btn-export i {
  font-size: 1rem;
}
</style>
